import { useMemo } from "react";
import {
  BookingQuestionId,
  BookingQuestionRequired,
} from "@b2bportal/rbc-activities-api";

export interface ActivityFieldValues {
  height?: string;
  weight?: string;
}

const ACTIVITY_BOOKING_QUESTION_IDS = {
  HEIGHT: BookingQuestionId.Height,
  WEIGHT: BookingQuestionId.Weight,
} as const;

interface BookingQuestion {
  id: string;
  label: string;
  required: string;
  units?: string[];
}

interface ActivityFieldConfig {
  show: boolean;
  required: boolean;
  label?: string;
  value?: string;
  unit?: string;
}

interface ActivityFieldsResult {
  height: ActivityFieldConfig;
  weight: ActivityFieldConfig;
}

export const useActivityFields = (
  bookingQuestions?: BookingQuestion[],
  savedActivityFieldValues?: ActivityFieldValues
): ActivityFieldsResult => {
  return useMemo(() => {
    const defaultConfig: ActivityFieldConfig = {
      show: false,
      label: undefined,
      required: false,
      value: undefined,
      unit: undefined,
    };

    if (!bookingQuestions?.length) {
      return {
        height: defaultConfig,
        weight: defaultConfig,
      };
    }

    const heightQuestion = bookingQuestions.find(
      (q) => q.id === ACTIVITY_BOOKING_QUESTION_IDS.HEIGHT
    );
    const weightQuestion = bookingQuestions.find(
      (q) => q.id === ACTIVITY_BOOKING_QUESTION_IDS.WEIGHT
    );

    const heightUnit = heightQuestion?.units?.[0] ?? "ft";
    const weightUnit = weightQuestion?.units?.[0] ?? "lbs";

    return {
      height: {
        show: !!heightQuestion,
        label: heightQuestion?.label,
        required:
          heightQuestion?.required === BookingQuestionRequired.Mandatory,
        value: savedActivityFieldValues?.height,
        unit: heightUnit,
      },
      weight: {
        show: !!weightQuestion,
        label: weightQuestion?.label,
        required:
          weightQuestion?.required === BookingQuestionRequired.Mandatory,
        value: savedActivityFieldValues?.weight,
        unit: weightUnit,
      },
    };
  }, [bookingQuestions, savedActivityFieldValues]);
};
